import { API_BASE } from '../../config/api';

export const INGEST_ENDPOINT = `${API_BASE}/api/roblox/session/flush`;

export function safeJsonParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

export function prettyDataForDisplay(data) {
  if (!data || typeof data !== 'object') return data;
  const cloned = JSON.parse(JSON.stringify(data));
  Object.keys(cloned).forEach((key) => {
    const value = cloned[key];
    const vec = extractVectorLikePosition(value);
    if (vec) {
      cloned[key] = `Vector3(${vec.x}, ${vec.y}, ${vec.z})`;
    } else if (value === null && /position/i.test(key)) {
      cloned[key] = 'null';
    }
  });
  return cloned;
}

export function formatUnixTime(ts) {
  if (!ts) return '-';
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString();
}

const SQL_UTC_DATETIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

export function parseDate(value) {
  if (typeof value === 'string' && SQL_UTC_DATETIME.test(value)) {
    return new Date(`${value.replace(' ', 'T')}Z`);
  }
  return new Date(value);
}

export function formatDate(ts) {
  if (!ts) return '-';
  const d = parseDate(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDurationFromSeconds(totalSeconds) {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '-';
  if (totalSeconds < 60) return `${Math.floor(totalSeconds)}s`;
  const totalMinutes = totalSeconds / 60;
  if (totalMinutes < 60) return `${Math.floor(totalMinutes)}m`;
  const totalHours = totalMinutes / 60;
  return `${Math.floor(totalHours)}h`;
}

export function getRetentionDaysFromPlanId(planId) {
  if (planId === 'enterprise') return 14;
  if (planId === 'pro') return 10;
  return 7;
}

export function getSessionLimitFromPlanId(planId) {
  if (planId === 'enterprise') return 50000;
  if (planId === 'pro') return 5000;
  return 500;
}

export function formatTimeLeft(targetDate) {
  if (!targetDate) return '-';
  const diffMs = targetDate.getTime() - Date.now();
  if (diffMs <= 0) return 'expired';
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h`;
  const mins = Math.max(1, Math.floor(diffMs / (1000 * 60)));
  return `${mins}m`;
}

export function buildSessionTimeline(flushes) {
  const items = [];

  flushes.forEach((flush) => {
    const actions = safeJsonParse(flush.actions_json, []);
    const positions = safeJsonParse(flush.positions_json, []);

    actions.forEach((action, index) => {
      items.push({
        kind: 'action',
        key: `a-${flush.id}-${index}`,
        flushId: flush.id,
        t: Number(action?.t || 0),
        actionType: action?.type || 'Unknown action',
        data: action?.data || {},
      });
    });

    positions.forEach((position, index) => {
      items.push({
        kind: 'position',
        key: `p-${flush.id}-${index}`,
        flushId: flush.id,
        t: Number(position?.t || 0),
        x: position?.x,
        y: position?.y,
        z: position?.z,
      });
    });
  });

  items.sort((a, b) => {
    if (a.t !== b.t) return a.t - b.t;
    if (a.kind === b.kind) return 0;
    return a.kind === 'action' ? -1 : 1;
  });

  return items;
}

export function normalizeTimestampSeconds(value) {
  if (value == null || value === '') return null;

  const asNumber = Number(value);
  if (Number.isFinite(asNumber) && asNumber > 0) {
    if (asNumber > 1000000000000) return Math.floor(asNumber / 1000);
    if (asNumber > 1000000000) return Math.floor(asNumber);
  }

  const parsed = parseDate(value).getTime();
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.floor(parsed / 1000);
  }

  return null;
}

export function getSessionDurationSeconds(session, timeline = []) {
  const timestamps = timeline
    .map((item) => normalizeTimestampSeconds(item.t))
    .filter((t) => Number.isFinite(t) && t > 0)
    .sort((a, b) => a - b);

  if (timestamps.length > 0) {
    return Math.max(0, timestamps[timestamps.length - 1] - timestamps[0]);
  }

  const joinTime = normalizeTimestampSeconds(session?.join_time);
  const quitTime = normalizeTimestampSeconds(session?.quit_time);
  if (Number.isFinite(joinTime) && Number.isFinite(quitTime) && quitTime >= joinTime) {
    return Math.max(0, quitTime - joinTime);
  }

  const updatedAtSeconds = normalizeTimestampSeconds(session?.updated_at);
  if (Number.isFinite(joinTime) && joinTime > 0 && Number.isFinite(updatedAtSeconds) && updatedAtSeconds >= joinTime) {
    return Math.max(0, updatedAtSeconds - joinTime);
  }

  return null;
}

export function getSessionDurationLabel(session, timeline) {
  const seconds = getSessionDurationSeconds(session, timeline);
  return seconds == null ? '-' : formatDurationFromSeconds(seconds);
}

export function extractVectorLikePosition(value) {
  if (!value || typeof value !== 'object') return null;
  const x = Number(value.x ?? value.X);
  const y = Number(value.y ?? value.Y ?? 0);
  const z = Number(value.z ?? value.Z);
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  return { x, y, z };
}

export function projectPositionsFromFlushes(flushes) {
  const points = [];

  flushes.forEach((flush) => {
    const positions = safeJsonParse(flush.positions_json, []);
    const actions = safeJsonParse(flush.actions_json, []);

    positions.forEach((position, index) => {
      if (!Number.isFinite(Number(position?.x)) || !Number.isFinite(Number(position?.z))) {
        return;
      }
      points.push({
        key: `pos-${flush.id}-${index}`,
        source: 'position',
        t: Number(position?.t || 0),
        x: Number(position?.x),
        y: Number(position?.y || 0),
        z: Number(position?.z),
      });
    });

    actions.forEach((action, index) => {
      const actionPos = extractVectorLikePosition(action?.data?.PositionShowInMapAnalytic);
      if (!actionPos) return;
      points.push({
        key: `action-pos-${flush.id}-${index}`,
        source: 'action',
        actionKey: `a-${flush.id}-${index}`,
        actionType: action?.type || 'Unknown action',
        t: Number(action?.t || 0),
        x: actionPos.x,
        y: actionPos.y,
        z: actionPos.z,
      });
    });
  });

  points.sort((a, b) => a.t - b.t);
  return points;
}

export function worldToMapPoint(point, mapConfig, width, height) {
  if (!point || !mapConfig || !width || !height) return null;
  const minX = Number(mapConfig.worldMinX);
  const maxX = Number(mapConfig.worldMaxX);
  const minZ = Number(mapConfig.worldMinZ);
  const maxZ = Number(mapConfig.worldMaxZ);
  const rotationDegrees = Number(mapConfig.rotationDegrees || 0);
  const flipX = Boolean(mapConfig.flipX);
  const flipZ = Boolean(mapConfig.flipZ);
  if (![minX, maxX, minZ, maxZ, rotationDegrees].every(Number.isFinite)) return null;
  if (maxX <= minX || maxZ <= minZ) return null;

  const centerX = (minX + maxX) / 2;
  const centerZ = (minZ + maxZ) / 2;
  const radians = (rotationDegrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const dx = Number(point.x) - centerX;
  const dz = Number(point.z) - centerZ;
  const rotatedX = centerX + (dx * cos - dz * sin);
  const rotatedZ = centerZ + (dx * sin + dz * cos);

  const u = (rotatedX - minX) / (maxX - minX);
  const v = (rotatedZ - minZ) / (maxZ - minZ);
  const finalU = flipX ? 1 - u : u;
  const finalV = flipZ ? 1 - v : v;

  return {
    x: Math.max(0, Math.min(1, finalU)) * width,
    y: Math.max(0, Math.min(1, finalV)) * height,
  };
}

export function buildRobloxSnippet(projectId, apiKey) {
  return `local HttpService = game:GetService("HttpService")\n\nlocal response = HttpService:RequestAsync({\n    Url = "${INGEST_ENDPOINT}",\n    Method = "POST",\n    Headers = {\n        ["Content-Type"] = "application/json",\n        ["X-Project-Id"] = "${projectId}",\n        ["X-Api-Key"] = "${apiKey}",\n    },\n    Body = HttpService:JSONEncode({\n        sessionId = self.sessionId,\n        userId = self.userId,\n        joinTime = self.joinTime,\n        isFinal = isFinal,
        quitTime = isFinal and os.time() or nil,\n        actions = self.actions,\n        positions = self.positions,\n        game = "Your Game Name",\n    }),\n})`;
}

export function collectTrackableLeafEntries(value, path = [], result = []) {
  if (value == null) return result;

  if (typeof value === 'number' && Number.isFinite(value)) {
    result.push({ path: path.join('.'), value, valueType: 'number' });
    return result;
  }

  if (typeof value === 'string' && value.trim()) {
    result.push({ path: path.join('.'), value: value.trim(), valueType: 'string' });
    return result;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      collectTrackableLeafEntries(item, [...path, String(index)], result);
    });
    return result;
  }

  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, child]) => {
      collectTrackableLeafEntries(child, [...path, key], result);
    });
  }

  return result;
}

export function buildTrackableFieldCatalog(timeline) {
  const catalog = new Map();

  timeline.forEach((item) => {
    if (item.kind !== 'action' || !item.data || typeof item.data !== 'object') return;

    const entries = collectTrackableLeafEntries(item.data);
    entries.forEach(({ path, valueType }) => {
      const fieldPath = path ? `${item.actionType}.${path}` : item.actionType;
      if (!catalog.has(fieldPath)) {
        catalog.set(fieldPath, {
          fieldPath,
          valueType,
          actionType: item.actionType,
          sampleCount: 0,
        });
      }
      const existing = catalog.get(fieldPath);
      existing.sampleCount += 1;
      if (!existing.valueType && valueType) {
        existing.valueType = valueType;
      }
    });
  });

  return Array.from(catalog.values()).sort((a, b) => a.fieldPath.localeCompare(b.fieldPath));
}

export function buildTrackedFieldSeries(timeline, fieldPath, valueType) {
  if (!fieldPath) return [];
  const points = [];

  timeline.forEach((item, index) => {
    if (item.kind !== 'action' || !item.data || typeof item.data !== 'object') return;
    const entries = collectTrackableLeafEntries(item.data);
    entries.forEach((entry) => {
      const entryPath = entry.path ? `${item.actionType}.${entry.path}` : item.actionType;
      if (entryPath !== fieldPath) return;
      if (valueType && entry.valueType !== valueType) return;
      points.push({
        x: index,
        t: Number(item.t || 0),
        value: entry.value,
        valueType: entry.valueType,
        actionType: item.actionType,
        timelineKey: item.key,
        flushId: item.flushId,
      });
    });
  });

  return points.sort((a, b) => (a.t !== b.t ? a.t - b.t : a.x - b.x));
}

export function buildLinePath(points, width, height, padding) {
  if (!Array.isArray(points) || points.length === 0) return '';
  const values = points.map((point) => point.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;
  const usableWidth = Math.max(1, width - padding * 2);
  const usableHeight = Math.max(1, height - padding * 2);

  return points
    .map((point, index) => {
      const x = padding + (points.length === 1 ? usableWidth / 2 : (usableWidth * index) / (points.length - 1));
      const y = height - padding - (((point.value - minValue) / range) * usableHeight);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
}

export function sampleIndices(total, target) {
  if (total <= 0 || target <= 0) return [];
  if (target >= total) return Array.from({ length: total }, (_, i) => i);
  if (target === 1) return [0];
  const out = new Set([0, total - 1]);
  const step = (total - 1) / (target - 1);
  for (let i = 1; i < target - 1; i += 1) {
    out.add(Math.round(i * step));
  }
  return Array.from(out).sort((a, b) => a - b);
}

export function buildDisplayMapPoints(points, selectedActionMapKey) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const total = points.length;
  if (total <= 250) return points;

  const focusIndex = selectedActionMapKey
    ? points.findIndex((point) => point.actionKey === selectedActionMapKey)
    : -1;

  const keep = new Set(sampleIndices(total, 180));
  if (focusIndex >= 0) {
    for (let i = Math.max(0, focusIndex - 12); i <= Math.min(total - 1, focusIndex + 12); i += 1) {
      keep.add(i);
    }
  }

  return Array.from(keep)
    .sort((a, b) => a - b)
    .map((index) => points[index])
    .filter(Boolean);
}
